// src/pages/inventory/InventoryHistory.js
import React, { useState, useEffect, Fragment } from "react";
import { Card, CardHeader, Col, Table, Input } from "reactstrap";
import { FormLabel, Modal, Popover, PopoverBody, PopoverHeader } from "react-bootstrap";
import { Filter, X } from "react-feather";
import Skeleton from "react-loading-skeleton";
import { Breadcrumbs, Popovers, ToolTip } from "../../AbstractElements";
import MyPagination from "../pagination";
// import InventoryDetailsModal from "./InventoryDetailsModal";
import { useTranslation } from "react-i18next";
import { useLoader } from "../../_helper/LoaderContext";
import { useNavigate } from "react-router-dom";
import { usePharmacy } from "../../_helper/PharmacyProvider";
import { getInventoryHistory } from "./InventoryService";
import { useAtom } from "jotai";
import {
  InventoryHistoryPageAtom,
  InventoryHistoryPageSizeAtom,
} from "./InventoryAtoms";

// Dummy static data for inventory history
export const inventoryHistoryData = [
  {
    id: 1,
    transactionType: "Increment", // or "Decrement"
    productName: "Paracetamol 500mg Tablet",
    manufacturer: "Cipla Ltd.",
    quantity: 50,
    currency: "$",
    price: 7.5,
    total: 375,
    date: "2025-08-10",
    note: "New stock arrived",
    tax: 5,
    discount: 0,
  },
  {
    id: 2,
    transactionType: "Decrement",
    productName: "Amoxicillin 250mg Capsule",
    manufacturer: "Sun Pharma",
    quantity: 10,
    currency: "$",
    price: 12,
    total: 120,
    date: "2025-08-12",
    note: "Sold 10 units",
    tax: 2,
    discount: 5,
  },
];

const InventoryHistory = () => {
  const [inventoryData, setInventoryData] = useState([]);
  const [page, setPage] = useAtom(InventoryHistoryPageAtom);
  const [size, setSize] = useAtom(InventoryHistoryPageSizeAtom);
  const [totalItems, setTotalItems] = useState(0);
  const [searchValue, setSearchValue] = useState("");
  const { setLoading } = useLoader();
  const [skeletonLoading, setSkeletonLoading] = useState(false);
  const [selectedInventory, setSelectedInventory] = useState(null);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [popover, setPopover] = useState(false);

  const { t } = useTranslation();
  const history = useNavigate();
  const { pharmacyPOS } = usePharmacy();

  const [editTooltipOpen, setEditTooltipOpen] = useState(false);
  const [filterTooltipOpen, setFilterTooltipOpen] = useState(false);
  const [clearTooltipOpen, setClearTooltipOpen] = useState(false);
  const [viewTooltipOpen, setViewTooltipOpen] = useState({});
  const toggleEditTooltip = () => setEditTooltipOpen(!editTooltipOpen);
  const toggleFilterTooltip = () => setFilterTooltipOpen(!filterTooltipOpen);
  const toggleClearTooltip = () => setClearTooltipOpen(!clearTooltipOpen);

  const [openInventoryItems, setOpenInventoryItems] = useState(null);

  const toggleViewTooltip = (id) => {
    setViewTooltipOpen((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const Toggle = () => setPopover(!popover);

  useEffect(() => {
    getInventories();
  }, [page, size]);

  const getInventories = async (overrides = {}) => {
    const body = {
      clinicId: pharmacyPOS?.hospital_id,
      page: overrides.page !== undefined ? overrides.page : page,
      limit: overrides.size !== undefined ? overrides.size : size,
      search: (overrides.search !== undefined ? overrides.search : searchValue).trim(),
    };
    try {
      setLoading(true);
      setSkeletonLoading(true);
      const response = await getInventoryHistory(body);
      if (response.data.data.inventory.length !== 0) {
        setInventoryData(response.data.data.inventory || []);
        const totalCount = response.data.data.total || 0;
        setTotalItems(Math.ceil(totalCount / (overrides.size || size)));
      } else {
        setInventoryData([]);
        setTotalItems(0);
      }
    } catch (error) {
      console.error("Error fetching sales history:", error);
    } finally {
      setLoading(false);
      setSkeletonLoading(false);
    }
  };

  const handleSearchButtonClick = () => {
    setPage(1);
    getInventories({ page: 1 });
  };

  const handleClear = () => {
    setSearchValue("");
    setPage(1);
    setSize(10);
    getInventories({ search: "", page: 1, size: 10 });
  };

  const handlePageChange = (pageNumber) => setPage(pageNumber);

  const handlePageSizeChange = (e) => {
    setSize(parseFloat(e.target.value));
    setPage(1);
  };

  const openDetailsModal = (inventory) => {
    setSelectedInventory(inventory);
    setDetailsModalOpen(true);
  };

  // Event handler for opening edit component
  const handleEditClick = (getID) => {
    // console.log(getID);
    history(`${process.env.PUBLIC_URL}/update-inventory/${getID}`);
  };

  // Event handler for opening view component
  const handleViewClick = (getID) => {
    // console.log(getID);
    history(`${process.env.PUBLIC_URL}/view-inventory/${getID}`);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, "0");
    const month = date.toLocaleString(undefined, { month: "short" });
    const year = date.getFullYear();
    return `${day} ${month} ${year}`; // Example: May 07, 2025
  };

  return (
    <Fragment>
      <Breadcrumbs
        mainTitle={t("Adjustment History")}
        parent={t("Dashboard")}
        title={t("Adjustment History")}
      />
      <Col sm="12">
        <Card>
          {/* <CardHeader className="d-flex justify-content-between align-items-center">
						<h5>{t("Inventory History")}</h5>
					</CardHeader> */}

          {/* Search & Filter */}
          <div
            className="row"
            style={{ alignItems: "center", padding: "12px 24px" }}
          >
            <Col sm="8" lg="8" xl="10" md="8">
              <Input
                type="text"
                placeholder={t("Search by, Adjustment No, Product, Manufacturer")}
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleSearchButtonClick();
                }}
              />
            </Col>
            <Col
              className="col-xl-2 col-lg-4 col-md-4 col-sm-4 d-flex justify-content-start align-items-center"
              style={{ gap: "18px" }}
            >
              <button
                id="FilterToolTip"
                className="btn btn-primary"
                style={{ height: "40px" }}
                onClick={handleSearchButtonClick}
              >
                <Filter />
              </button>
              <ToolTip
                attrToolTip={{
                  placement: "top",
                  isOpen: filterTooltipOpen,
                  target: "FilterToolTip",
                  toggle: toggleFilterTooltip,
                }}
              >
                <strong>{t("Filter")}</strong>
              </ToolTip>
              <button
                id="ClearToolTip"
                className="btn btn-secondary"
                style={{ height: "40px" }}
                onClick={handleClear}
              >
                <X />
              </button>
              <ToolTip
                attrToolTip={{
                  placement: "top",
                  isOpen: clearTooltipOpen,
                  target: "ClearToolTip",
                  toggle: toggleClearTooltip,
                }}
              >
                <strong>{t("Clear")}</strong>
              </ToolTip>
            </Col>
          </div>
        </Card>
        <Card>
          {/* Table */}
          <div className="card-block">
            <div className="row" style={{ padding: "12px 24px" }}>
              <Col sm="12">
                <div className="rounded table-responsive">
                  <Table className="mb-0 table-center table-bordered table-hover">
                    <thead>
                      <tr>
                        <th>{t("Adjustment No")}</th>
                        <th>{t("Product")}</th>
                        <th>{t("Manufacturer")}</th>
                        <th>{t("Reason")}</th>
                        <th>{t("Date")}</th>
                        <th>{t("Adjustment Type")}</th>
                        <th className="text-center">{t("Action")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {skeletonLoading ? (
                        [...Array(7)].map((_, i) => (
                          <tr key={i}>
                            {Array(7)
                              .fill()
                              .map((_, idx) => (
                                <td key={idx}>
                                  <Skeleton height={20} />
                                </td>
                              ))}
                          </tr>
                        ))
                      ) : inventoryData.length > 0 ? (
                        inventoryData.map((inv, index) => (
                          <tr key={index}>
                            <td>{inv.inventoryNumber}</td>
                            <td>
                              {/* {inv.inventoryItems?.slice(0, 2).map((item, i) => (
                                <div key={i} className="mb-1">
                                  <div className="fw-semibold">{item.medicineName}</div>
                                  <small className="text-muted">
                                    {item.brandName} | Qty: {item.quantity}
                                  </small>
                                </div>
                              ))}
                              {inv.inventoryItems?.length > 2 && (
                                <small className="text-primary">
                                  +{inv.inventoryItems.length - 2} more
                                </small>
                              )} */}
                              <div className="items-list">
                                {inv.inventoryItems && inv.inventoryItems.length > 0 ? (
                                  <div>
                                    <strong className="d-block mb-1">
                                      {inv.inventoryItems[0].brandName}
                                      <span className="badge badge-light-light rounded-5 ms-2">
                                        {inv.inventoryItems[0].quantity}
                                      </span>
                                    </strong>
                                    <small className="text-muted d-block" style={{ maxWidth: "280px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                                      title={inv.inventoryItems[0].medicineName}
                                    >
                                      {inv.inventoryItems[0].medicineName}
                                    </small>
                                    {inv.inventoryItems.length > 1 && (
                                      <small
                                        className="text-primary" style={{ cursor: 'pointer' }}
                                        onClick={() => setOpenInventoryItems(inv)}
                                      >
                                        + {inv.inventoryItems.length - 1} {t("View All")}
                                      </small>

                                    )}
                                  </div>
                                ) : (
                                  <span className="text-muted">{t("No items")}</span>
                                )}
                              </div>
                            </td>
                            <td>
                              {inv.inventoryItems?.slice(0, 2).map((item, i) => (
                                <div key={i} className="mb-1">
                                  <div className="fw-semibold">{item.manufacturer}</div>
                                </div>
                              ))}
                              {inv.inventoryItems?.length > 2 && (
                                <>
                                  <small className="text-primary tw-cursor-pointer" id={`popover-${inv.id}`} onClick={() => setPopover(!popover)}>
                                    +{inv.inventoryItems.length - 2} more
                                  </small>
                                  <Popovers placement='top' isOpen={popover} target={`popover-${inv.id}`} toggle={Toggle} title={t("Manufacturers")}>
                                    {inv.inventoryItems?.map((item, i) => (
                                      <div key={i} className="mb-1">
                                        <div className="bg-body-secondary rounded-2 px-2 py-1">{item.manufacturer}</div>
                                      </div>
                                    ))}
                                  </Popovers>
                                </>
                              )}
                            </td>
                            <td>{inv.adjustmentReason}</td>
                            <td>{formatDate(inv.adjustmentDate)}</td>
                            <td>
                              <span
                                className={`badge rounded-pill px-3 py-2 d-inline-flex align-items-center gap-1 ${inv.adjustmentType === "add"
                                  ? "bg-success"
                                  : "bg-danger"
                                  }`}
                              >
                                {inv.adjustmentType === "add" ? (
                                  <>
                                    <i className="fa fa-plus-circle"></i>{" "}
                                    {t("Added")}
                                  </>
                                ) : (
                                  <>
                                    <i className="fa fa-minus-circle"></i>{" "}
                                    {t("Deducted")}
                                  </>
                                )}
                              </span>
                            </td>
                            {/* <td className="text-center"> */}
                            {/* <button className='success-action-button me-1' id="EditToolTip" onClick={() => handleEditClick(inv?.id)}> <i className="fa fa-pencil-square" style={{ fontSize: "18px" }}></i> <ToolTip attrToolTip={{ placement: 'top', isOpen: editTooltipOpen, target: 'EditToolTip', toggle: toggleEditTooltip, }} > <strong>{t("Edit")}</strong> </ToolTip> </button> */}
                            {/* <button
																className="success-action-button"
																id={`ViewToolTip-${index}`}
																onClick={() => handleViewClick(inv?._id)}
															>
																<i className="fa fa-eye" style={{ fontSize: "18px" }}></i>
																<ToolTip
																	attrToolTip={{
																		placement: "top",
																		isOpen: viewTooltipOpen,
																		target: `ViewToolTip-${index}`,
																		toggle: toggleViewTooltip,
																	}}
																>
																	<strong>{t("View")}</strong>
																</ToolTip>
															</button>
														</td> */}
                            <td className="text-center">
                              <button
                                className="success-action-button"
                                id={`ViewToolTip-${index}`}
                                onClick={() => handleViewClick(inv?._id)}
                              >
                                <i
                                  className="fa fa-eye"
                                  style={{ fontSize: "18px" }}
                                ></i>
                              </button>
                              <ToolTip
                                attrToolTip={{
                                  placement: "top",
                                  isOpen: viewTooltipOpen[index] || false,
                                  target: `ViewToolTip-${index}`,
                                  toggle: () => toggleViewTooltip(index),
                                }}
                              >
                                <strong>{t("View")}</strong>
                              </ToolTip>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr className="text-center">
                          <td colSpan="5">
                            {t("No inventory records available.")}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>
              </Col>

              {/* Pagination */}
              <Col sm="12" className="mt-4">
                <div className="text-center d-md-flex align-items-center justify-content-between">
                  <div className="pt-3 show-entries">
                    <FormLabel>{t("Show")}&nbsp;</FormLabel>
                    <select
                      value={size}
                      onChange={handlePageSizeChange}
                      className="form-control"
                      style={{ display: "unset", width: "auto" }}
                    >
                      <option value={5}>5</option>
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={30}>30</option>
                      <option value={50}>50</option>
                    </select>
                    &nbsp;
                    <FormLabel>{t("entries")}</FormLabel>
                  </div>
                  <MyPagination
                    totalPages={totalItems}
                    currentPage={page}
                    onPageChange={handlePageChange}
                  />
                </div>
              </Col>
            </div>
          </div>
        </Card>
      </Col>

      {/* Details Modal */}
      {/* {selectedInventory && (
        <InventoryDetailsModal
          show={detailsModalOpen}
          handleClose={() => setDetailsModalOpen(false)}
          inventory={selectedInventory}
        />
      )} */}
      <Modal show={!!openInventoryItems} onHide={() => setOpenInventoryItems(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{t("Added Items")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {openInventoryItems?.inventoryItems.map((item, i) => (
            <div key={i} className={`mb-2 ${i + 1 === openInventoryItems.inventoryItems.length ? '' : 'border-bottom'}`}>
              <strong>{item.brandName}
                <span className="badge badge-light-light rounded-5 ms-2">
                  {item.quantity}
                </span>
              </strong>
              <div className="text-muted small">{item.medicineName}</div>
            </div>
          ))}
        </Modal.Body>
      </Modal>
    </Fragment>
  );
};

export default InventoryHistory;
